import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ExpandMoreIcon } from 'applicaiton/assets/expand-more.svg';
import useOnClickOutside from 'common/hooks/useClickOutside';
import Input from '../Input';
import Checkbox from '../Checkbox';
import { DropdownProps, Option } from './models';
import {
  DropdownStyled,
  OptionCheckBox,
  OptionItem,
  OptionsList,
} from './styles';

/**
 * Dropdown Component
 *
 * A custom dropdown component that allows users to select one or multiple options from a list.
 *
 * @param id - The unique identifier for the dropdown component
 * @param label - The label text for the dropdown component
 * @param placeholder - The placeholder text for the dropdown component when no option is selected
 * @param searchPlaceholder - The placeholder text for the search input when the withSearch prop is true
 * @param value - The current value(s) of the dropdown component, either a string or an array of strings
 * @param onChange - A callback function that is called whenever the selected option changes
 * @param hint - Optional hint text to be displayed below the dropdown component
 * @param disabled - Boolean flag indicating whether the dropdown component is disabled
 * @param options - An array of Option objects representing the available options in the dropdown list
 * @param isMulti - Boolean flag indicating whether multiple options can be selected in the dropdown component
 * @param errorMessage - Optional error message to be displayed below the dropdown component if an error occurs
 * @param withSearch - Boolean flag indicating whether a search input should be included above the dropdown list
 * @param LeftIcon - Optional icon to display on the left side of the dropdown component
 */
const Dropdown: FC<DropdownProps> = ({
  id,
  label,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  hint,
  disabled,
  options,
  isMulti,
  errorMessage,
  withSearch,
  LeftIcon,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [searchItem, setSearchItem] = useState('');

  useOnClickOutside(ref, () => setIsActive(false));

  const optionList = useMemo(() => {
    if (!searchItem) return options;
    return options.filter((item) =>
      item.label.toLocaleLowerCase().includes(searchItem.toLocaleLowerCase()),
    );
  }, [options, searchItem]);

  const handleChange = (selectedItem: string) => {
    if (!Array.isArray(value)) return;
    const valueAlreadyAdded = value.some((item) => item === selectedItem);
    if (valueAlreadyAdded) {
      const filtredValue = value.filter((item) => item !== selectedItem);
      onChange(filtredValue);
    } else {
      onChange([...value, selectedItem]);
    }
    return;
  };

  /**
   * Checks if a given option's checkbox is checked
   *
   * @param item - The Option object representing the current option
   * @returns Boolean flag indicating whether the checkbox for the current option is checked
   */
  const IsValueChecked = (item: Option) => {
    const isValueArray = Array.isArray(value);
    return isValueArray && value.some((i) => i === item.value);
  };

  /**
   * Returns the display value of the dropdown component based on its current state and selected options
   *
   * @returns The display value of the dropdown component as a string
   */
  const getValue = () => {
    if (isMulti) return `${value.length} ${t('selected')}`;
    const currentOption = optionList.find(
      (item) => String(item.value) === String(value),
    );
    return currentOption?.label;
  };

  /**
   * Checks if the options list should be displayed
   *
   * @returns Boolean flag indicating whether the options list should be displayed
   */
  const isListNotEmpty = isActive && !!options.length;

  return (
    <DropdownStyled
      isActive={isActive}
      disabled={disabled}
      ref={ref}
      isError={!!errorMessage}
    >
      {label && <label htmlFor={id}>{label}</label>}
      <section onMouseDown={() => !disabled && setIsActive(!isActive)}>
        {LeftIcon ? (
          <div>
            <LeftIcon />
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </div>
        ) : (
          <>
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </>
        )}
        <ExpandMoreIcon />
      </section>
      {isListNotEmpty && !isMulti ? (
        <OptionsList className="optionList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item) => (
            <OptionItem
              key={item.value}
              onClick={() => {
                onChange(item);
                setIsActive(false);
              }}
              selected={value === String(item.value)}
            >
              {item.label}
            </OptionItem>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {isListNotEmpty && isMulti ? (
        <OptionsList className="optionListMulti">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item, index) => (
            <OptionCheckBox
              key={`${item.value}_${index}`}
              htmlFor={`${item.label}_${index}`}
              selected={IsValueChecked(item)}
            >
              <Checkbox
                id={`${item.label}_${index}`}
                checked={IsValueChecked(item)}
                onChange={() => handleChange(String(item.value))}
              />
              {item.label}
            </OptionCheckBox>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {(hint || errorMessage) && (
        <span>{errorMessage ? errorMessage : hint}</span>
      )}
    </DropdownStyled>
  );
};

export default Dropdown;
